import React, { useContext, useEffect, useState } from "react";
import SectionHeader from "../components/section/SectionHeader";
import { FAULTS_TABLE_HEADERS, VIEW_FAULTS_FORM_FIELDS } from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  transformFaultResponseRow,
  transformFaultsRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import { getFaultByRef } from "../api";
import ViewRow from "../components/dataTable/ViewRow";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import { Lang, Page, Operation } from "../translations/types";
import { getPageTranslationText } from "../translations/util";

const translations = getPageTranslationText({
  lang: Lang.EN,
  page: Page.FAULTS,
  op: Operation.READ,
});

const Faults = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  useAppTitle("Faults - IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [faultsData, setFaultsData] = useState<any[]>([]);
  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    pageNum: number;
    pageSize: number;
  }>({
    pageSize: 20,
    pageNum: 1,
  });

  const fetchFaults = async () => {
    setLoading(true);
    try {
      const { pageNum, pageSize } = searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/fault-mgmt/faults?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setFaultsData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <>
      <SectionHeader
        pageTitle={translations.header}
        pageDescription={translations.headerSubtext}
        sideComponent={null}
      />

      {showSidePanel && selectedConnectionId ? (
        <ViewRow
          panelName={translations.viewRow.panelName}
          org={selectedConnectionId}
          esRef={selectedConnectionId}
          setShowEditPanel={handleToggleSidePanel}
          formFields={VIEW_FAULTS_FORM_FIELDS}
          transformResponseRow={transformFaultResponseRow}
          loadDataByRef={getFaultByRef}
        />
      ) : null}

      <DataTableWithControls
        loading={loading}
        showTableControls={false}
        filterColumns={[]}
        selectedColumn={null}
        setSelectedColumn={() => null}
        onSearch={() => fetchFaults()}
        showCreateButton={false}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows={transformFaultsRows(faultsData).map((row: any) => ({
          ...row,
          action: (
            <TableActionButton
              onActionButtonClick={() => {
                setSelectedConnectionId(row.id);
                handleToggleSidePanel();
              }}
            />
          ),
        }))}
        headers={FAULTS_TABLE_HEADERS}
        pageNum={searchQuery.pageNum}
        setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
        totalItems={numberOfRows}
        totalPages={numberOfPages}
        pageSize={searchQuery.pageSize}
        setPageSize={(pageSize) => setSearhQuery({ ...searchQuery, pageSize })}
      />
    </>
  );
};

export default Faults;
